import { useState } from 'react';
import { Check, Calculator, Award, HelpCircle } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';

// Atomic masses
const ATOMIC_MASS_SN = 118.71;
const ATOMIC_MASS_O = 16.00;

const stepInfoContent: Record<number, { title: string; content: string }> = {
  1: {
    title: 'Finding Oxygen Mass',
    content: 'The mass of oxygen is found by difference: Mass of product (crucible + oxide - crucible alone) minus the original tin mass. This works because the only elements present are tin (which you measured) and oxygen (which came from the air/reaction).'
  },
  2: {
    title: 'Calculating Moles of Tin',
    content: 'Convert mass to moles using: moles = mass / molar mass. For tin, the molar mass is 118.71 g/mol. Moles represent the actual number of atoms (scaled by Avogadros number), which we need for the ratio.'
  },
  3: {
    title: 'Calculating Moles of Oxygen',
    content: 'Similarly, convert oxygen mass to moles using its atomic mass of 16.00 g/mol. Note we use atomic oxygen (O) not molecular oxygen (O2) because in the compound, oxygen exists as individual oxide ions.'
  },
  4: {
    title: 'Finding the Mole Ratio',
    content: 'Divide both mole values by the smaller one to get a simple whole-number ratio. For SnO2, you should get approximately 1:2. Small deviations from whole numbers are due to experimental error - round to the nearest integer.'
  },
  5: {
    title: 'Determining Empirical Formula',
    content: 'The empirical formula shows the simplest whole-number ratio of atoms. SnO2 indicates one tin atom for every two oxygen atoms. This corresponds to tin(IV) oxide, where tin has a +4 oxidation state.'
  }
};

export default function Part8Calculations() {
  // Simulated experimental data
  const crucibleMass = 25.3456;
  const tinMass = 1.0123;
  const finalMass = 26.6312; // Crucible + SnO2

  const [oxygenMassCalculated, setOxygenMassCalculated] = useState(false);
  const [molesSnCalculated, setMolesSnCalculated] = useState(false);
  const [molesOCalculated, setMolesOCalculated] = useState(false);
  const [ratioFound, setRatioFound] = useState(false);
  const [formulaDetermined, setFormulaDetermined] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Calculated values
  const massOfProduct = finalMass - crucibleMass;
  const massOfOxygen = massOfProduct - tinMass;
  const molesOfSn = tinMass / ATOMIC_MASS_SN;
  const molesOfO = massOfOxygen / ATOMIC_MASS_O;
  const smallerMoles = Math.min(molesOfSn, molesOfO);
  const ratioSn = molesOfSn / smallerMoles;
  const ratioO = molesOfO / smallerMoles;
  const roundedRatioSn = Math.round(ratioSn);
  const roundedRatioO = Math.round(ratioO);

  const handleCalculateOxygenMass = () => {
    if (currentStep === 1) {
      setOxygenMassCalculated(true);
      setCurrentStep(2);
    }
  };

  const handleCalculateMolesSn = () => {
    if (currentStep === 2) {
      setMolesSnCalculated(true);
      setCurrentStep(3);
    }
  };

  const handleCalculateMolesO = () => {
    if (currentStep === 3) {
      setMolesOCalculated(true);
      setCurrentStep(4);
    }
  };

  const handleFindRatio = () => {
    if (currentStep === 4) {
      setRatioFound(true);
      setCurrentStep(5);
    }
  };

  const handleDetermineFormula = () => {
    if (currentStep === 5) {
      setFormulaDetermined(true);
    }
  };

  const completedCount = [oxygenMassCalculated, molesSnCalculated, molesOCalculated, ratioFound, formulaDetermined].filter(Boolean).length;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <Calculator size={16} />
          <h3>Part VIII: Calculations</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Calculate Mass of Oxygen', hint: 'By difference', done: oxygenMassCalculated },
            { id: 2, title: 'Calculate Moles of Tin', hint: 'mass / molar mass', done: molesSnCalculated },
            { id: 3, title: 'Calculate Moles of Oxygen', hint: 'mass / molar mass', done: molesOCalculated },
            { id: 4, title: 'Find Mole Ratio', hint: 'Divide by smaller', done: ratioFound },
            { id: 5, title: 'Determine Empirical Formula', hint: 'SnₓOᵧ', done: formulaDetermined }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Experimental Data Summary */}
        <div className="data-summary">
          <h4>Experimental Data</h4>
          <div className="data-row">
            <span>Crucible mass:</span>
            <span className="value">{crucibleMass.toFixed(3)} g</span>
          </div>
          <div className="data-row">
            <span>Tin mass:</span>
            <span className="value">{tinMass.toFixed(3)} g</span>
          </div>
          <div className="data-row">
            <span>Final mass:</span>
            <span className="value">{finalMass.toFixed(3)} g</span>
          </div>
        </div>
      </div>

      <LabBench
        experimentName="Calculate Empirical Formula"
        completedCount={completedCount}
        totalSteps={5}
        equipment={['Calculator', 'Data table']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="calculations-workspace">
          {/* Step 1: Mass of Oxygen */}
          <div className={`calc-card ${currentStep === 1 ? 'active' : ''} ${oxygenMassCalculated ? 'completed' : ''}`}>
            <h4>Step 1: Mass of Oxygen</h4>
            <div className="calc-formula">
              <span>Mass of O = (Final mass - Crucible mass) - Mass of Sn</span>
            </div>
            {oxygenMassCalculated ? (
              <div className="calc-result">
                <span>Mass of O = ({finalMass.toFixed(3)} - {crucibleMass.toFixed(3)}) - {tinMass.toFixed(3)}</span>
                <span className="answer">= {massOfOxygen.toFixed(3)} g</span>
              </div>
            ) : currentStep === 1 && (
              <button className="calc-btn" onClick={handleCalculateOxygenMass}>
                <Calculator size={16} />
                Calculate
              </button>
            )}
          </div>

          {/* Step 2: Moles of Tin */}
          <div className={`calc-card ${currentStep === 2 ? 'active' : ''} ${molesSnCalculated ? 'completed' : ''}`}>
            <h4>Step 2: Moles of Tin</h4>
            <div className="calc-formula">
              <span>Moles Sn = Mass Sn / {ATOMIC_MASS_SN} g/mol</span>
            </div>
            {molesSnCalculated ? (
              <div className="calc-result">
                <span>Moles Sn = {tinMass.toFixed(3)} / {ATOMIC_MASS_SN}</span>
                <span className="answer">= {molesOfSn.toFixed(6)} mol</span>
              </div>
            ) : currentStep === 2 && (
              <button className="calc-btn" onClick={handleCalculateMolesSn}>
                <Calculator size={16} />
                Calculate
              </button>
            )}
          </div>

          {/* Step 3: Moles of Oxygen */}
          <div className={`calc-card ${currentStep === 3 ? 'active' : ''} ${molesOCalculated ? 'completed' : ''}`}>
            <h4>Step 3: Moles of Oxygen</h4>
            <div className="calc-formula">
              <span>Moles O = Mass O / {ATOMIC_MASS_O} g/mol</span>
            </div>
            {molesOCalculated ? (
              <div className="calc-result">
                <span>Moles O = {massOfOxygen.toFixed(3)} / {ATOMIC_MASS_O}</span>
                <span className="answer">= {molesOfO.toFixed(6)} mol</span>
              </div>
            ) : currentStep === 3 && (
              <button className="calc-btn" onClick={handleCalculateMolesO}>
                <Calculator size={16} />
                Calculate
              </button>
            )}
          </div>

          {/* Step 4: Mole Ratio */}
          <div className={`calc-card ${currentStep === 4 ? 'active' : ''} ${ratioFound ? 'completed' : ''}`}>
            <h4>Step 4: Mole Ratio</h4>
            <div className="calc-formula">
              <span>Divide both by the smaller value ({smallerMoles.toFixed(6)})</span>
            </div>
            {ratioFound ? (
              <div className="calc-result">
                <div className="ratio-display">
                  <div className="ratio-item">
                    <span>Sn: {ratioSn.toFixed(2)}</span>
                    <span className="rounded">≈ {roundedRatioSn}</span>
                  </div>
                  <span className="ratio-separator">:</span>
                  <div className="ratio-item">
                    <span>O: {ratioO.toFixed(2)}</span>
                    <span className="rounded">≈ {roundedRatioO}</span>
                  </div>
                </div>
              </div>
            ) : currentStep === 4 && (
              <button className="calc-btn" onClick={handleFindRatio}>
                <Calculator size={16} />
                Calculate Ratio
              </button>
            )}
          </div>

          {/* Step 5: Empirical Formula */}
          <div className={`calc-card final ${currentStep === 5 ? 'active' : ''} ${formulaDetermined ? 'completed' : ''}`}>
            <h4>Step 5: Empirical Formula</h4>
            {formulaDetermined ? (
              <div className="formula-result">
                <Award size={32} className="trophy" />
                <div className="formula-display">
                  <span className="formula-text">
                    Sn<sub>{roundedRatioSn > 1 ? roundedRatioSn : ''}</sub>O<sub>{roundedRatioO}</sub>
                  </span>
                  <span className="formula-name">
                    {roundedRatioO === 2 ? 'Tin(IV) Oxide' : 'Tin(II) Oxide'}
                  </span>
                </div>
              </div>
            ) : currentStep === 5 && (
              <button className="calc-btn final-btn" onClick={handleDetermineFormula}>
                <Award size={16} />
                Determine Formula
              </button>
            )}
          </div>

          {/* Final Summary */}
          {formulaDetermined && (
            <div className="final-summary">
              <h3>Experiment Complete!</h3>
              <p>
                The empirical formula of the tin oxide formed is <strong>SnO₂</strong> (tin IV oxide).
              </p>
              <p>
                This confirms that tin reacted with oxygen in a 1:2 mole ratio,
                indicating tin has an oxidation state of +4 in this compound.
              </p>
            </div>
          )}
        </div>

        <style>{`
          .calculations-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            padding: 20px;
            width: 100%;
            max-width: 500px;
          }

          .data-summary {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .data-summary h4 {
            margin: 0 0 8px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .data-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            padding: 3px 0;
          }

          .data-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .calc-card {
            width: 100%;
            background: var(--bg-surface);
            border: 2px solid var(--border);
            border-radius: 12px;
            padding: 16px;
            opacity: 0.5;
            transition: all 0.3s;
          }

          .calc-card.active {
            opacity: 1;
            border-color: var(--primary);
          }

          .calc-card.completed {
            opacity: 1;
            border-color: var(--secondary);
          }

          .calc-card h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: var(--text-primary);
          }

          .calc-formula {
            font-family: monospace;
            font-size: 12px;
            color: var(--text-secondary);
            margin-bottom: 12px;
            padding: 8px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .calc-result {
            display: flex;
            flex-direction: column;
            gap: 4px;
            font-family: monospace;
            font-size: 12px;
          }

          .calc-result .answer {
            color: var(--secondary);
            font-weight: 700;
            font-size: 14px;
          }

          .calc-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            width: 100%;
            padding: 10px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
          }

          .calc-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .ratio-display {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 16px;
          }

          .ratio-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .ratio-item .rounded {
            font-size: 18px;
            font-weight: 700;
            color: var(--secondary);
          }

          .ratio-separator {
            font-size: 24px;
            font-weight: 700;
            color: var(--text-muted);
          }

          .calc-card.final.completed {
            border-color: gold;
            background: linear-gradient(135deg, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0.05) 100%);
          }

          .formula-result {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 16px;
            padding: 16px;
          }

          .trophy {
            color: gold;
          }

          .formula-display {
            text-align: center;
          }

          .formula-text {
            font-size: 36px;
            font-weight: 700;
            color: var(--text-primary);
          }

          .formula-name {
            display: block;
            font-size: 14px;
            color: var(--text-secondary);
            margin-top: 4px;
          }

          .final-btn {
            background: gold;
            color: #333;
          }

          .final-btn:hover {
            background: #FFD700;
          }

          .final-summary {
            background: rgba(0, 168, 107, 0.15);
            border: 2px solid var(--secondary);
            border-radius: 12px;
            padding: 24px;
            text-align: center;
          }

          .final-summary h3 {
            margin: 0 0 12px 0;
            color: var(--secondary);
          }

          .final-summary p {
            margin: 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp11"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
